/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from "vuex";
import session from "../../../../share/session";
import individualDiagnose from "../../../../share/util/individualDiagnose";
import individualWorkSupportUtil from "../../../../share/util/individualWorkSupportUtil";
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED, } from "../../../../share/util/api/polling";

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  EXECUTION: 'execution',
  CLEAR_FAILED: 'clearFailed',
  SUCCESS: 'success',
  FAILED: 'failed',
};

export default {
  name: 'requirement034',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      systemId: '',
      isCallExit: false,
      // ダイアログフラグ
      modal_requirement_034: false,
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED:
        case PHASE.CLEAR_FAILED:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.EXECUTION:
        default:
          return 'secondary';
      }
    },
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_034 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
    },

    /** 待機の「OK」ボタン押下 */
    async standbyProcess() {
      await this.standby();
    },

    /** クリア失敗 の「OK」ボタン押下 */
    async executeProcess() {
      this.phase = PHASE.EXECUTION;
      await this.execute();
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /**NonDefaultSession遷移+セキュリティアクセス */
    async standby() {
      try {
        this.isLoading = true;
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'standby'
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );
        if (response) {
          switch (response.data.status) {
            case REQUEST_STATUS_COMPLETED:
              this.phase = PHASE.EXECUTION;
              await this.execute();
              break;
            default:
              this.showUnexpectedError();
              break;
          }
        }
      }
      finally {
        this.isCallExit = true;
        this.isLoading = false;
      }
    },

    /** ルーチン実行処理 */
    async execute() {
      try {
        this.isLoading = true;
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'execute'
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );
        if (response) {
          switch (response.data.status) {
            case REQUEST_STATUS_COMPLETED:
              // 終了条件の場合
              if (response.data.user_data.routine_status === '0002') {
                this.phase = PHASE.SUCCESS;
                return;
              }
              this.phase = PHASE.CLEAR_FAILED;
              break;
            case REQUEST_STATUS_FAILED:
              // 異常終了の場合
              this.phase = PHASE.CLEAR_FAILED;
              break;
            default:
              this.showUnexpectedError();
              break;
          }
        }
      }
      finally {
        this.isLoading = false;
      }
    },

    /** クリア失敗 の「キャンセル」ボタン押下 /正常終了 の「OK」ボタン押下/
      * 共通エラーの「OK」ボタン押下 */
    async exitProcess() {
      // DefaultSession遷移
      if (this.isCallExit) {
        this.isCallExit = false;
        await this.exit();
      } else {
        this.closeWizard();
      }
    },

    //終了処理
    async exit() {
      try {
        this.isLoading = true;
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'exit'
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );
        if (response) {
          if (response.data.status !== REQUEST_STATUS_COMPLETED) {
            this.showUnexpectedError();
            return;
          }
          this.closeWizard();
        }
      }
      finally {
        this.isLoading = false;
      }
    },

    /** その他エラーの表示 */
    showUnexpectedError() {
      if (this.phase === PHASE.FAILED) {
        this.closeWizard();
        return;
      }
      this.phase = PHASE.FAILED;
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_034 = false;
    },
  },

  async mounted() {
    this.init();
  },
};
